import { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useTranslation } from 'react-i18next';
import {
  Search,
  ArrowRight,
  Calendar,
  Sparkles,
  ShieldCheck,
  Filter,
} from 'lucide-react';
import { Link, useNavigate } from 'react-router-dom';
import {
  useCategories,
  useSearch,
  MarketplaceCategory,
  SalonSearchResult,
} from '../api/use-marketplace';
import { SiteShell } from '../components/site-shell';
import { SalonCard, SalonCardSkeleton } from '../components/salon-card';
import { CityCombobox } from '../components/city-combobox';
import { detectCountryDomain } from '../lib/country-domain';

/**
 * Marketplace home — Treatwell/Fresha-style:
 *
 *   1. Charcoal hero with subtle dot pattern, sans-serif wordmark + tagline,
 *      large search card.
 *   2. "Browse by category" card grid (icon-on-pill style) → results
 *      header with count + filter button → animated salon grid.
 *   3. Light "Why Salunas" features → dark CTA band → footer.
 *
 * Modern marketplace look: bold sans-serif, charcoal/cognac accent,
 * generous spacing, rounded-2xl cards. No serif, no italics. No fake
 * stats, no fake ratings, no discount badges (locked decision).
 */
export function HomePage() {
  const [selectedCategory, setSelectedCategory] = useState<string>('all');
  const cats = useCategories();

  return (
    <SiteShell>
      <Hero />
      <BrowseSection
        categories={cats.data}
        categoriesLoading={cats.isLoading}
        selectedCategory={selectedCategory}
        onSelectCategory={setSelectedCategory}
      />
      <Features />
      <FinalCta />
    </SiteShell>
  );
}

// ─── Hero ──────────────────────────────────────────────────────────────

function Hero() {
  const { t } = useTranslation();
  const [service, setService] = useState('');
  // Pre-fill the city from the country domain (e.g. Brussels on .be / .com),
  // never a Belgian default on another country's domain (salunas.pt → empty).
  const [city, setCity] = useState(detectCountryDomain().defaultCity);
  const nav = useNavigate();
  const ease = [0.22, 1, 0.36, 1] as const;

  const submit = (e: React.FormEvent) => {
    e.preventDefault();
    const params = new URLSearchParams();
    if (service.trim()) params.set('q', service.trim());
    if (city.trim()) params.set('city', city.trim());
    nav(`/search?${params.toString()}`);
  };

  return (
    <section className="relative bg-gradient-to-br from-zinc-950 via-zinc-900 to-zinc-800 text-white">
      {/* Decorative layer is its own clipping context so dropdowns rendered
          inside the hero (e.g. the city autocomplete) can spill below the
          hero edge without being cut off. */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        {/* Subtle dot pattern */}
        <div
          className="absolute inset-0 opacity-[0.10]"
          style={{
            backgroundImage:
              'radial-gradient(white 1px, transparent 1px)',
            backgroundSize: '24px 24px',
          }}
        />
        {/* Warm cognac glow in the lower-right corner to keep the brand cue */}
        <div
          className="absolute -bottom-32 -right-32 w-[600px] h-[600px] rounded-full opacity-30 blur-3xl"
          style={{ background: 'hsl(20, 65%, 38%)' }}
        />
      </div>

      <div className="relative z-10 max-w-5xl mx-auto px-5 sm:px-6 pt-20 sm:pt-24 md:pt-28 pb-16 sm:pb-20 md:pb-24 text-center">
        <motion.div
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.05, ease }}
          className="inline-flex items-center justify-center gap-2 mb-5 sm:mb-6"
        >
          <Sparkles className="w-5 h-5 sm:w-6 sm:h-6 text-cognac-400" />
          <span className="text-sm font-medium uppercase tracking-[0.22em] text-white/80">
            {t('home.eyebrow')}
          </span>
        </motion.div>

        <motion.h1
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 0.15, ease }}
          className="text-4xl sm:text-5xl md:text-6xl lg:text-7xl font-bold tracking-tight leading-[1.05]"
        >
          {t('home.titleA')}{' '}
          <span className="text-cognac-400">{t('home.titleB')}</span>
        </motion.h1>

        <motion.p
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 0.7, delay: 0.35, ease }}
          className="mt-5 sm:mt-6 text-base sm:text-lg md:text-xl text-white/80 max-w-2xl mx-auto"
        >
          {t('home.subtitle')}
        </motion.p>

        {/* Search */}
        <motion.form
          onSubmit={submit}
          initial={{ opacity: 0, y: 24 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.7, delay: 0.45, ease }}
          className="mt-10 sm:mt-12 max-w-3xl mx-auto"
        >
          <div className="flex flex-col gap-2 md:flex-row md:items-stretch md:gap-0 bg-white border border-white/10 rounded-2xl shadow-2xl p-2">
            <SearchField
              icon={<Search className="w-5 h-5 text-zinc-400" />}
              placeholder={t('home.searchServicePlaceholder')}
              value={service}
              onChange={setService}
            />
            <div className="hidden md:block w-px bg-zinc-200 my-2" />
            <CityCombobox
              value={city}
              onChange={setCity}
              placeholder={t('home.searchLocationPlaceholder')}
              style="bare"
              iconWidth="w-5 h-5"
            />
            <button
              type="submit"
              className="w-full md:w-auto inline-flex items-center justify-center gap-2 rounded-xl bg-zinc-900 hover:bg-zinc-800 text-white font-semibold text-sm px-7 py-3.5 transition-colors"
            >
              {t('common.search')}
              <ArrowRight className="w-4 h-4" />
            </button>
          </div>
        </motion.form>
      </div>
    </section>
  );
}

function SearchField({
  icon,
  placeholder,
  value,
  onChange,
}: {
  icon: React.ReactNode;
  placeholder: string;
  value: string;
  onChange: (v: string) => void;
}) {
  return (
    <label className="flex-1 flex items-center gap-3 px-4 py-3 md:py-2 cursor-text">
      <span className="flex-shrink-0">{icon}</span>
      <input
        type="text"
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
        className="w-full bg-transparent text-sm md:text-base text-foreground placeholder:text-muted-foreground focus:outline-none"
      />
    </label>
  );
}

// ─── Browse ────────────────────────────────────────────────────────────

function BrowseSection({
  categories,
  categoriesLoading,
  selectedCategory,
  onSelectCategory,
}: {
  categories: MarketplaceCategory[] | undefined;
  categoriesLoading: boolean;
  selectedCategory: string;
  onSelectCategory: (key: string) => void;
}) {
  const { t } = useTranslation();
  const country = detectCountryDomain().code;
  const search = useSearch({
    category: selectedCategory === 'all' ? null : selectedCategory,
    country,
    size: 18,
  });
  const total = search.data?.total ?? 0;
  const activeCat = categories?.find((c) => c.key === selectedCategory);

  return (
    <section id="browse" className="bg-background">
      <div className="max-w-7xl mx-auto px-5 sm:px-6 py-14 sm:py-16 md:py-20">
        <div className="flex items-center justify-between flex-wrap gap-4 mb-8 sm:mb-10">
          <h2 className="text-2xl sm:text-3xl font-bold text-foreground">
            {t('home.browseHeading')}
          </h2>
          <Link
            to="/search"
            className="hidden sm:inline-flex items-center gap-2 px-4 py-2 rounded-xl border border-border text-sm font-medium hover:bg-secondary transition-colors"
          >
            <Filter className="w-4 h-4" />
            {t('home.allFilters')}
          </Link>
        </div>

        <CategoryGrid
          categories={categories}
          loading={categoriesLoading}
          selected={selectedCategory}
          onSelect={onSelectCategory}
        />

        {/* Results header */}
        <div className="mt-12 sm:mt-14 mb-6 flex items-end justify-between flex-wrap gap-3">
          <div>
            <h3 className="text-xl sm:text-2xl font-bold text-foreground">
              {search.isLoading
                ? t('home.resultsLooking')
                : total === 0
                  ? t('home.resultsNoMatches')
                  : t('home.resultsSalon', { count: total })}
            </h3>
            <p className="text-sm text-muted-foreground mt-0.5">
              {selectedCategory === 'all'
                ? t('home.allCategories')
                : activeCat?.displayName ?? selectedCategory}
            </p>
          </div>
        </div>

        <SalonResults
          loading={search.isLoading}
          salons={search.data?.results ?? []}
          selectedCategory={selectedCategory}
          categories={categories}
        />
      </div>
    </section>
  );
}

function CategoryGrid({
  categories,
  loading,
  selected,
  onSelect,
}: {
  categories: MarketplaceCategory[] | undefined;
  loading: boolean;
  selected: string;
  onSelect: (key: string) => void;
}) {
  const { t } = useTranslation();
  if (loading) {
    return (
      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-7 gap-3">
        {Array.from({ length: 7 }).map((_, i) => (
          <div key={i} className="h-32 rounded-2xl bg-muted/60 animate-pulse" />
        ))}
      </div>
    );
  }
  if (!categories) return null;

  const tiles: Array<{ key: string; displayName: string }> = [
    { key: 'all', displayName: t('home.categoryAll') },
    ...categories.filter((c) => c.key !== 'other'),
  ];

  return (
    <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-7 gap-3">
      {tiles.map((c) => {
        const active = c.key === selected;
        return (
          <motion.button
            key={c.key}
            type="button"
            whileTap={{ scale: 0.97 }}
            onClick={() => onSelect(c.key)}
            className={[
              'group flex flex-col items-center justify-center gap-2.5 h-32 rounded-2xl border bg-card p-4 text-center transition-all duration-200',
              active
                ? 'border-foreground bg-foreground/[0.04] shadow-md'
                : 'border-border/60 hover:border-foreground/40 hover:shadow-sm',
            ].join(' ')}
          >
            <span className="text-3xl leading-none" aria-hidden>
              {categoryGlyph(c.key)}
            </span>
            <h4 className="text-sm sm:text-base font-semibold text-foreground leading-tight">
              {c.displayName}
            </h4>
          </motion.button>
        );
      })}
    </div>
  );
}

/** Modern lucide glyphs as text — keeps the BE category keys without a
 *  hardcoded icon map per category. */
function categoryGlyph(key: string): string {
  switch (key) {
    case 'all':     return '✦';
    case 'hair':    return '✂︎';
    case 'barber':  return '✦';
    case 'nails':   return '♢';
    case 'beauty':  return '✿';
    case 'massage': return '♨︎';
    case 'spa':     return '❀';
    default:        return '✦';
  }
}

function SalonResults({
  loading,
  salons,
  selectedCategory,
  categories,
}: {
  loading: boolean;
  salons: SalonSearchResult[];
  selectedCategory: string;
  categories: MarketplaceCategory[] | undefined;
}) {
  if (loading) {
    return (
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
        {Array.from({ length: 6 }).map((_, i) => <SalonCardSkeleton key={i} />)}
      </div>
    );
  }
  if (salons.length === 0) {
    return <SalonsEmptyState category={selectedCategory} categories={categories} />;
  }
  return (
    <motion.div
      className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6"
      layout
    >
      <AnimatePresence mode="popLayout">
        {salons.map((s, i) => (
          <motion.div
            key={s.tenantId}
            layout
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.96 }}
            transition={{ duration: 0.35, delay: i * 0.04, ease: [0.22, 1, 0.36, 1] }}
          >
            <SalonCard salon={s} />
          </motion.div>
        ))}
      </AnimatePresence>
    </motion.div>
  );
}

function SalonsEmptyState({
  category,
  categories,
}: {
  category: string;
  categories: MarketplaceCategory[] | undefined;
}) {
  const { t } = useTranslation();
  const label =
    category === 'all'
      ? null
      : categories?.find((c) => c.key === category)?.displayName ?? category;
  return (
    <div className="rounded-2xl border border-dashed border-border bg-secondary/30 p-12 md:p-16 text-center">
      <div className="text-5xl mb-4">🔍</div>
      <h3 className="text-xl sm:text-2xl font-bold text-foreground mb-2">
        {label
          ? t('home.salonsNoCategory', { label: label.toLowerCase() })
          : t('home.salonsNoListed')}
      </h3>
      <p className="text-muted-foreground max-w-md mx-auto">
        {t('home.salonsCuratingBody')}
      </p>
    </div>
  );
}

// ─── Features ──────────────────────────────────────────────────────────

function Features() {
  const { t } = useTranslation();
  const items = [
    {
      icon: <Search className="w-7 h-7" />,
      title: t('home.featureDiscoveryTitle'),
      copy: t('home.featureDiscoveryCopy'),
    },
    {
      icon: <Calendar className="w-7 h-7" />,
      title: t('home.featureBookingTitle'),
      copy: t('home.featureBookingCopy'),
    },
    {
      icon: <ShieldCheck className="w-7 h-7" />,
      title: t('home.featureQualityTitle'),
      copy: t('home.featureQualityCopy'),
    },
  ];
  return (
    <section className="bg-secondary/40 border-t border-border/40">
      <div className="max-w-7xl mx-auto px-5 sm:px-6 py-16 sm:py-20 md:py-24">
        <div className="text-center mb-12 sm:mb-14">
          <h2 className="text-2xl sm:text-3xl md:text-4xl font-bold text-foreground mb-3">
            {t('home.featuresHeading')}
          </h2>
          <p className="text-base sm:text-lg text-muted-foreground max-w-2xl mx-auto">
            {t('home.featuresSubtitle')}
          </p>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          {items.map((item) => (
            <div
              key={item.title}
              className="bg-card border border-border/60 rounded-2xl p-7 sm:p-8 transition-all duration-200 hover:border-foreground/20 hover:shadow-md text-center"
            >
              <div className="inline-flex items-center justify-center w-14 h-14 rounded-2xl bg-foreground/[0.06] text-foreground mb-5">
                {item.icon}
              </div>
              <h3 className="text-lg sm:text-xl font-bold text-foreground mb-2">
                {item.title}
              </h3>
              <p className="text-sm sm:text-base text-muted-foreground leading-relaxed">
                {item.copy}
              </p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

// ─── Final CTA ─────────────────────────────────────────────────────────

function FinalCta() {
  const { t } = useTranslation();
  return (
    <section className="relative overflow-hidden bg-gradient-to-br from-zinc-900 via-zinc-900 to-zinc-800 text-white">
      <div
        className="absolute inset-0 opacity-[0.08] pointer-events-none"
        style={{
          backgroundImage:
            'radial-gradient(white 1px, transparent 1px)',
          backgroundSize: '24px 24px',
        }}
      />
      <div
        className="pointer-events-none absolute -top-32 -left-32 w-[500px] h-[500px] rounded-full opacity-25 blur-3xl"
        style={{ background: 'hsl(20, 65%, 38%)' }}
      />
      <div className="relative z-10 max-w-3xl mx-auto px-5 sm:px-6 py-20 sm:py-24 md:py-28 text-center">
        <h2 className="text-3xl sm:text-4xl md:text-5xl font-bold leading-tight">
          {t('home.ctaHeading')}
        </h2>
        <p className="mt-5 text-base sm:text-lg text-white/80 max-w-xl mx-auto">
          {t('home.ctaSubtitle')}
        </p>
        <div className="mt-10 flex flex-col sm:flex-row gap-3 justify-center">
          <Link
            to="/search"
            className="inline-flex items-center justify-center gap-2 px-7 py-3.5 rounded-xl bg-white text-zinc-900 font-semibold text-sm hover:bg-white/90 transition-colors"
          >
            <Calendar className="w-4 h-4" />
            {t('home.ctaPrimary')}
          </Link>
          <Link
            to="/login"
            className="inline-flex items-center justify-center px-7 py-3.5 rounded-xl border border-white/30 text-white hover:bg-white/10 font-semibold text-sm transition-colors"
          >
            {t('home.ctaSecondary')}
          </Link>
        </div>
      </div>
    </section>
  );
}
